/**
 * Number Crunch — combine six source numbers with + − × ÷ to reach a target.
 * Each tile can be consumed once; every operation produces a new tile.
 *
 * Ported from iOS `NumberCrunchModels.swift` / `NumberCrunchViewModel.swift`.
 */

export type CrunchOp = '+' | '−' | '×' | '÷';

export const CRUNCH_OPS: CrunchOp[] = ['+', '−', '×', '÷'];

/** Upper bound on any intermediate value, matching `CrunchOperation.apply`. */
const MAX_VALUE = 99999;

/**
 * Returns null when the operation is illegal — the iOS rules are strict:
 * subtraction must stay positive, division must be exact, and sums/products
 * cannot exceed 99999.
 */
export function applyOp(op: CrunchOp, a: number, b: number): number | null {
  switch (op) {
    case '+': {
      const r = a + b;
      return r > MAX_VALUE ? null : r;
    }
    case '−':
      return a > b ? a - b : null;
    case '×': {
      const r = a * b;
      return r > MAX_VALUE ? null : r;
    }
    case '÷':
      if (b === 0 || a % b !== 0) return null;
      return a / b;
  }
}

/** Message shown when an operation is rejected — mirrors the iOS toasts. */
export function rejectionMessage(op: CrunchOp): string {
  switch (op) {
    case '÷':
      return 'Not evenly divisible';
    case '−':
      return 'Result must be positive';
    default:
      return 'Invalid operation';
  }
}

export interface NumberTile {
  id: number;
  value: number;
  isOriginal: boolean;
  isUsed: boolean;
}

export interface CrunchStep {
  leftValue: number;
  rightValue: number;
  op: CrunchOp;
  result: number;
  leftTileId: number;
  rightTileId: number;
  resultTileId: number;
}

export interface SolutionStep {
  a: number;
  b: number;
  op: CrunchOp;
  result: number;
}

export interface NumberCrunchPuzzle {
  sourceNumbers: number[];
  target: number;
  solution: SolutionStep[];
}

/** Raw Firestore shape. Note the field is `sources`, not `sourceNumbers`. */
export interface NumberCrunchLevel {
  target?: unknown;
  sources?: unknown[];
  sourceNumbers?: unknown[];
  solution?: Array<{ a?: unknown; b?: unknown; op?: unknown; result?: unknown }>;
  maxSteps?: number;
}

function intValue(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.trunc(raw);
  if (typeof raw === 'string') {
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

/**
 * The published `op` is the typographic minus/times ("−", "×"), but ASCII
 * variants show up in older documents — normalise both.
 */
function normalizeOp(raw: unknown): CrunchOp | null {
  const s = String(raw ?? '').trim();
  if (s === '+') return '+';
  if (s === '−' || s === '-') return '−';
  if (s === '×' || s === '*' || s.toLowerCase() === 'x') return '×';
  if (s === '÷' || s === '/') return '÷';
  return null;
}

export function normalizePuzzle(
  raw: NumberCrunchLevel | null,
): NumberCrunchPuzzle | null {
  if (!raw) return null;

  const target = intValue(raw.target);
  if (target === null) return null;

  const rawSources = raw.sources ?? raw.sourceNumbers ?? [];
  const sourceNumbers = rawSources
    .map(intValue)
    .filter((n): n is number => n !== null);
  if (!sourceNumbers.length) return null;

  const solution: SolutionStep[] = [];
  for (const step of raw.solution ?? []) {
    const a = intValue(step?.a);
    const b = intValue(step?.b);
    const result = intValue(step?.result);
    const op = normalizeOp(step?.op);
    if (a === null || b === null || result === null || op === null) continue;
    solution.push({ a, b, op, result });
  }

  return { sourceNumbers, target, solution };
}

/** Distance from the target to the nearest still-available tile. */
export function distanceFromTarget(tiles: NumberTile[], target: number): number | null {
  const values = tiles.filter((t) => !t.isUsed).map((t) => t.value);
  if (!values.length) return null;
  return Math.min(...values.map((v) => Math.abs(v - target)));
}

/** 3 stars on the nose, 2 within 5, 1 within 10. */
export function starRating(tiles: NumberTile[], target: number): number {
  const dist = distanceFromTarget(tiles, target);
  if (dist === null) return 0;
  if (dist === 0) return 3;
  if (dist <= 5) return 2;
  if (dist <= 10) return 1;
  return 0;
}
